import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Loading, PageableDataSourceV2, PageableListV2Dto, TemplateDirective, getId } from '@fleet-beat/core';
import { InfiniteListItemWidth, InfiniteListParamsV2 } from '@fleet-beat/infinite-list';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { InfiniteListScrollDirective } from '../derectives/infinite-list-scroll.directive';

@Component({
  selector: 'fb-infinite-list',
  templateUrl: './infinite-list.component.html',
  styleUrls: ['./infinite-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/* eslint-disable-next-line @typescript-eslint/no-explicit-any*/
export class InfiniteListComponent<Data extends { id: string }, Params extends InfiniteListParamsV2<any>>
  extends OnDestroyMixin
  implements OnInit
{
  @Input() set outerScroll(value: InfiniteListScrollDirective) {
    this.outerScrollSubject.next(value);
  }
  get outerScroll(): InfiniteListScrollDirective {
    return this.outerScrollSubject.value;
  }

  @Input()
  set pageableDataSource(value: PageableDataSourceV2<Observable<PageableListV2Dto<Data>>, Params>) {
    if (value) {
      this.dataSource = value;
      this.applyData(value);
    }
  }
  get pageableDataSource(): PageableDataSourceV2<Observable<PageableListV2Dto<Data>>, Params> {
    return this.dataSource;
  }

  @Input() itemsWidth: string | InfiniteListItemWidth | undefined;
  @Input() itemsHorizontalOffset: number = 16;
  @Input() itemsVerticalOffset: number = 8;
  @Input() itemsTemplate: string = 'itemsTemplate';
  @Input() itemsContainerCssClass: string;
  @Input() headerTemplate: string;
  @Input() selectable: boolean = false;
  @Input() selectedItemId: string = null;
  @Input() limit: number = 10;
  @Input() noDataMessage: string;
  @Input() templates: TemplateDirective<object>[];

  @Input() externalDataSource: boolean = false;
  @Input() set externalItems(value: Data[]) {
    this.itemsSubject.next(value);
  }

  @Output() selectionChangedEvent: EventEmitter<Data> = new EventEmitter();

  set endOfData(value: boolean) {
    this.endOfDataValue = value;
    if (this.outerScrollSubject.value) {
      this.outerScrollSubject.value.endOfData = value;
    }
  }
  get endOfData(): boolean {
    return this.endOfDataValue;
  }

  itemsSubject: BehaviorSubject<Data[]> = new BehaviorSubject([]);
  loading: Loading;

  @ContentChildren(TemplateDirective) childrenTemplates: QueryList<TemplateDirective<object>>;

  private loadingActive: boolean = false;
  private endOfDataValue: boolean = false;
  private scrollEventSubscription: Subscription = new Subscription();
  private dataSource: PageableDataSourceV2<Observable<PageableListV2Dto<Data>>, Params>;
  private outerScrollSubject: BehaviorSubject<InfiniteListScrollDirective> = new BehaviorSubject(undefined);
  private lastPage: PageableListV2Dto<Data> = undefined;

  constructor(private matIconRegistry: MatIconRegistry, private domSanitizer: DomSanitizer) {
    super();
    this.matIconRegistry.addSvgIcon(
      'no_items',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg/no_items.svg'),
    );
  }

  ngOnInit(): void {
    this.outerScrollSubject
      .pipe(untilComponentDestroyed(this), filter(Boolean))
      .subscribe((scroll: InfiniteListScrollDirective) => {
        scroll.endOfData = this.endOfData;
        this.handleScrollEvent(scroll);
      });

    this.loadNext();
  }

  select(item: Data): void {
    this.selectedItemId = this.trackBy(item);
    this.selectionChangedEvent.emit(item);
  }

  loadNext(): void {
    if (this.loadingActive) {
      return;
    }
    this.dataSource.reloadSubject.next({
      params: {
        limit: this.lastPage.limit ? this.lastPage.limit : this.limit,
        offset: this.lastPage?.offset + this.lastPage?.limit,
      },
    } as Params);
  }

  trackBy(item: Data): string {
    return getId(item);
  }

  private handleScrollEvent(scroll: InfiniteListScrollDirective): void {
    this.scrollEventSubscription.unsubscribe();
    this.scrollEventSubscription = new Subscription();

    this.scrollEventSubscription.add(
      scroll.scrollEvent
        .pipe(
          untilComponentDestroyed(this),
          tap(() => this.loadNext()),
        )
        .subscribe(),
    );
  }

  private applyData(value: PageableDataSourceV2<Observable<PageableListV2Dto<Data>>, Params>): void {
    this.dataSource = value;
    this.loading = value.loading;

    this.loading.active$.pipe(untilComponentDestroyed(this)).subscribe((active: boolean) => {
      this.loadingActive = active;
    });

    if (this.externalDataSource) return;

    value.reloadSubject
      .pipe(
        untilComponentDestroyed(this),
        filter((params: Params) => params?.params?.offset === 0),
      )
      .subscribe(() => {
        this.itemsSubject.next([]);
        this.lastPage = undefined;
      });

    value.dataSource.pipe(untilComponentDestroyed(this)).subscribe((data: PageableListV2Dto<Data>) => {
      this.itemsSubject.next([...this.itemsSubject.value, ...data.data]);
      this.lastPage = data;

      this.endOfData = data.limit + data.offset > data.total;
    });
  }
}
